/** Saved repository status names; membership is displayed without reevaluation. */
export const STATUSES = ["NotEvaluated", "NotConfigured", "Inside", "Outside", "Unknown", "EvaluationFailed"];
export const statusLabel = (status) => ({
    Inside: "Inside geofence", Outside: "Outside geofences", Unknown: "Location unknown",
    EvaluationFailed: "Evaluation failed", NotEvaluated: "Not evaluated", NotConfigured: "Not configured",
}[status] || status || "Not evaluated");
export const statusExplanation = (status) => ({
    Unknown: "Location membership could not be determined from the saved GPS information.",
    EvaluationFailed: "The geofence evaluation could not be completed. See the saved reason for troubleshooting.",
}[status] || "");
/** Decodes policy flags without making claims about notification delivery or mode. */
export function suppressionLabel(flags = 0) {
    const reasons = [];
    if (flags & 1) reasons.push("Camera–ICX mismatch");
    if (flags & 2) reasons.push("Geofence policy");
    if (flags & ~3) reasons.push("Unrecognised policy flags");
    return reasons.length ? reasons.join(" + ") : "No suppression policy recorded";
}
/** Validates GeoJSON/Mapbox longitude, latitude pairs, preserving valid zero values. */
export function validPosition(lng, lat) {
    return Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lng) <= 180 && Math.abs(lat) <= 90;
}
/** Selects current boundaries by saved match ID, preserving renamed definitions without recalculating membership. */
export function locationsForMatches(locations, matches = []) {
    const ids = new Set((matches || []).map(match => String(match.geofenceId)));
    return locations.filter(location => ids.has(String(location.id)));
}
/**
 * Converts a current definition into map geometry; never evaluates event membership.
 * Circles use the stored metre radius on a sphere. Polygons retain GeoJSON order.
 * Returns null for malformed definitions so their list rows remain inspectable.
 */
export function geofenceFeature(location) {
    let geometry;
    if (location.shapeType === 0 || location.shapeType === "Circle") {
        const { centreLongitude: lng, centreLatitude: lat, radiusMetres } = location;
        if (!validPosition(lng, lat) || !Number.isFinite(radiusMetres) || radiusMetres <= 0) return null;
        // Great-circle destination points: angular distance uses metres / Earth radius.
        const distance = radiusMetres / 6371008.8;
        const phi = lat * Math.PI / 180;
        const lambda = lng * Math.PI / 180;
        const ring = Array.from({ length: 65 }, (_, i) => {
            const bearing = i * 2 * Math.PI / 64;
            const p = Math.asin(Math.sin(phi) * Math.cos(distance)
                + Math.cos(phi) * Math.sin(distance) * Math.cos(bearing));
            const l = lambda + Math.atan2(Math.sin(bearing) * Math.sin(distance) * Math.cos(phi),
                Math.cos(distance) - Math.sin(phi) * Math.sin(p));
            return [l * 180 / Math.PI, p * 180 / Math.PI];
        });
        ring[ring.length - 1] = [...ring[0]];
        geometry = { type: "Polygon", coordinates: [ring] };
    } else {
        try {
            const parsed = JSON.parse(location.polygonGeoJson);
            geometry = parsed.type === "Feature" ? parsed.geometry : parsed;
            if (!["Polygon", "MultiPolygon"].includes(geometry.type)) return null;
            const rings = geometry.type === "Polygon" ? geometry.coordinates : geometry.coordinates.flat();
            if (!rings.length || rings.some(ring => ring.length < 4
                || ring[0][0] !== ring[ring.length - 1][0]
                || ring[0][1] !== ring[ring.length - 1][1])) return null;
            const points = geometry.coordinates.flat(geometry.type === "Polygon" ? 1 : 2);
            if (!points.length || points.some(([lng, lat]) => !validPosition(lng, lat))) return null;
        } catch { return null; }
    }
    return { type: "Feature", geometry, properties: {
        id: location.id, label: location.label, enabled: location.isEnabled,
    } };
}
